import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofActionSuccessful } from '@ngxs/store';
import gql from 'graphql-tag';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { LayoutRoute } from 'src/app/_core/services/layout.service';
import { Logger } from 'src/app/_core/tools/logger';
import { LoginAction } from 'src/app/_user/store/user/user.action';
import { activityTypeParam } from 'src/app/board/activity/activity-routing.module';
import {
    getRouteActivityTypeFromActivityType,
    RouteActivityType,
    TypeActivite
} from 'src/app/board/activity/activity.constants';
import { arrayEmpty } from 'src/app/utils/array.utils';
import { isInEnumValues } from 'src/app/utils/enum.utils';
import { getParamFromActivatedRoute } from 'src/app/utils/routes.utils';
import {
    ActiviteDto,
    MutationGetIdActiviteCommandeArgs,
    MutationGetIdActiviteFactureArgs,
    MutationGetListeActiviteGeneriqueArgs
} from 'src/graphql/generated-types';

interface ActivityId {
    activityId: number;
}

interface InvoiceId {
    invoiceId: number;
}

interface OrderId {
    orderId: number;
}

type ActivityTypeFn = () => RouteActivityType;

export type RouteActivityTypeFrom = RouteActivityType
    | TypeActivite
    | ActivityId
    | InvoiceId
    | OrderId
    | ActivatedRoute
    | ActivityTypeFn;

interface Activity {
    id: number;
    typeCode: string;
}

@Injectable({
    providedIn: 'root'
})
export class ActivityNavigationService {

    private readonly logger = new Logger(this.constructor.name);

    /**
     * Liste des activités accessibles
     * @description Permet de récupérer le code du type d'une activité par rapport à un identifiant d'activité
     * Réinitialisé à chaque connexion
     * @private
     */
    private activitiesList: Activity[] = [];

    /**
     * Dictionnaire [orderId, RouteActivityType]
     * @description Permet de ne pas avoir a faire d'appel si le type d'activité de la commande a déjà été récupéré
     * Réinitialisé à chaque connexion
     * @private
     */
    private ordersActivitiesMap = new Map<number, RouteActivityType>();

    /**
     * Dictionnaire [invoiceId, RouteActivityType]
     * @description Permet de ne pas avoir a faire d'appel si le type d'activité de la facture a déjà été récupéré
     * Réinitialisé à chaque connexion
     * @private
     */
    private invoicesActivitiesMap = new Map<number, RouteActivityType>();

    constructor(
        private readonly graphqlService: GraphqlService,
        private readonly actions$: Actions,
    ) {
        actions$.pipe(
            ofActionSuccessful(LoginAction),
            tap(() => {
                this.ordersActivitiesMap.clear();
                this.invoicesActivitiesMap.clear();
            }),
            switchMap(() => this.getActivitiesList$())
        ).subscribe();
    }

    /**
     * Construction de la route pour la navigation vers un écran activité
     * @description Ajoute éventuellement le path 'activity' et le path du type d'activité
     * @example
     * this.layoutService.navigate({
     *     activityTypeFrom: RouteActivityType.Sales,
     *     routes: { content: 'orders/list' }
     * })
     * this.layoutService.navigate({
     *     activityTypeFrom: RouteActivityType.Sales,
     *     routes: { content: ['activity', 'orders', 'list'] }
     * })
     * this.layoutService.navigate({
     *     // activityTypeFrom n'est pas renseigné, mais est présent dans le path de la route
     *     routes: { content: 'activity/sales/orders/list' }
     * })
     * // Tous les exemples ci-dessus redirigeront sur 'activity/sales/orders/list'
     * @param activityType Source de donnée à partir de laquelle on veut récupérer {@link RouteActivityType}
     * @param routes Routes définies lors de la navigation
     * @param activatedRoute Routes actives lors de la navigation {@link ActivatedRoute}
     */
    constructRoutes$(activityType: RouteActivityTypeFrom, routes: LayoutRoute): Observable<LayoutRoute> {
        const constructActivityRoutes = (route: string | string[], routeActivityType: RouteActivityType): string[] => {
            const paths = ((typeof route === 'string')
                ? route.split('/') || []
                : route).filter(path => !!path);

            if (arrayEmpty(paths) || paths[0] === 'empty') {
                return paths;
            }

            const [activityPath] = paths;
            if (activityPath?.toLowerCase() !== 'activity') {
                // Ajoute 'activity' en première position si non existant.
                // ['orders', 'list'] -> devient -> ['activity', 'orders', 'list']
                paths.unshift('activity');
            }

            const [, activityTypePath] = paths;
            if (activityTypePath?.toLowerCase() !== routeActivityType) {
                if (isInEnumValues(activityTypePath, RouteActivityType)) {
                    // Si le path en deuxième position correspond à un RouteActivityType, on ne le modifie pas.
                    // ['activity', 'sales', 'orders', 'list'] ne change pas
                    this.logger.debug(`second segment "${activityTypePath}" is directly matching a known route activity type, no override`)();
                    return paths;
                }

                // Ajoute en deuxième position le type d'activité si non existant
                // ['activity', 'orders', 'list'] -> devient -> ['activity', 'sales', 'orders', 'list']
                paths.splice(1, 0, routeActivityType);
            }

            return paths;
        };

        return this.getActivityRouteParameter$(activityType).pipe(
            map(routeActivityType => {

                const newRoutes: LayoutRoute = {};
                Object.keys(routes).forEach(outlet => {
                    newRoutes[outlet] = constructActivityRoutes(routes[outlet], routeActivityType);
                });

                return newRoutes;
            })
        );
    }

    /**
     * Récupère {@link RouteActivityType} pour un code {@link TypeActivite}.
     * @param activityType Code {@link TypeActivite} pour lequel on souhaite récupérer son {@link RouteActivityType}
     * @private
     */
    getRouteActivityTypeFromActivityType(activityType: TypeActivite): RouteActivityType | undefined {
        return getRouteActivityTypeFromActivityType(activityType);
    }

    /**
     * Récupère {@link RouteActivityTypeFrom} à partir de plusieurs sources de données
     * - {@link RouteActivityTypeFrom} - Directement RouteActivityTypeFrom
     * - {@link TypeActivite} - Code du type d'activité
     * - {@link ActivityId} - Identifiant d'activité
     * - {@link OrderId} - Identifiant d'une commande d'activité
     * - {@link InvoiceId} - Identifiant d'une facture d'activité
     * @param activityType Source de donnée à partir de laquelle on veut récupérer {@link RouteActivityType}
     * @param activatedRoute Routes actives lors de la navigation {@link ActivatedRoute}
     * @private
     */
    private getActivityRouteParameter$(activityType: RouteActivityTypeFrom): Observable<RouteActivityType | undefined> {
        if (typeof activityType === 'function') {
            return of(activityType());
        }

        if (typeof activityType === 'object') {
            if ('activityId' in activityType) {
                return this.getRouteActivityTypeFromActivityId$(activityType.activityId);
            }

            if ('invoiceId' in activityType) {
                return this.getRouteActivityTypeFromInvoiceId$(activityType.invoiceId);
            }

            if ('orderId' in activityType) {
                return this.getRouteActivityTypeFromOrderId$(activityType.orderId);
            }

            if ('url' in activityType) {
                const type = getParamFromActivatedRoute(activityType, activityTypeParam) as RouteActivityType;
                if (isInEnumValues(type, RouteActivityType)) {
                    return of(type);
                }
                this.logger.debug(`routeActivityType ${type} is not a member of RouteActivityType enum`)();
            }
        }

        if (typeof activityType === 'string') {
            if (isInEnumValues(activityType, RouteActivityType)) {
                return of(activityType as RouteActivityType);
            }

            if (isInEnumValues(activityType, TypeActivite)) {
                return of(this.getRouteActivityTypeFromActivityType(activityType as TypeActivite));
            }
        }

        this.logger.debug('No RouteActivityType has been found because no match could be done.')();
        return of(undefined);
    }

    /**
     * Récupère {@link RouteActivityType} pour un identifiant d'activité donné.
     * Lorsque {@link activitiesList} est vide, effectue un appel GraphQL pour récupérer la liste.
     * @param activityId Identifiant de l'activité pour laquelle on souhaite récupérer son {@link RouteActivityType}
     * @private
     */
    private getRouteActivityTypeFromActivityId$(activityId: number): Observable<RouteActivityType | undefined> {
        const getRouteActivityType = (): RouteActivityType | undefined => {
            const activity = this.activitiesList.find(a => a.id === activityId);

            if (!activity) {
                this.logger.error(`No activity found for activityId: ${activityId}`)();
                return undefined;
            }

            return this.getRouteActivityTypeFromActivityType(activity.typeCode as TypeActivite);
        };

        if (arrayEmpty(this.activitiesList)) {
            return this.getActivitiesList$().pipe(
                map(() => getRouteActivityType())
            );
        }

        return of(getRouteActivityType());
    }

    /**
     * Récupère {@link RouteActivityType} pour une facture donnée depuis {@link invoicesActivitiesMap}
     * ou effectue un appel GraphQL pour récupérer son identifiant de type d'activité,
     * puis récupère le code de type d'activité depuis {@link activitiesList} pour récupérer son {@link RouteActivityType} correspondant
     * @param invoiceId Identifiant de la facture à partir de laquelle on veut récupérer son {@link RouteActivityType}
     * @private
     */
    private getRouteActivityTypeFromInvoiceId$(invoiceId: number): Observable<RouteActivityType | undefined> {
        if (this.invoicesActivitiesMap.has(invoiceId)) {
            return of(this.invoicesActivitiesMap.get(invoiceId));
        }

        return this.graphqlService.mutateOne<'GetIdActiviteFacture', MutationGetIdActiviteFactureArgs>({
            mutation: gql`
                mutation GetIdActiviteFacture($contexte: ContextInfosInput, $idFacture: Float) {
                    GetIdActiviteFacture(
                        contexte: $contexte
                        idFacture: $idFacture
                    )
                }
            `,
            variables: {
                idFacture: invoiceId
            }
        }).pipe(
            switchMap(activityId => this.getRouteActivityTypeFromActivityId$(activityId)),
            tap(routeActivityType => this.invoicesActivitiesMap.set(invoiceId, routeActivityType))
        );
    }

    /**
     * Récupère {@link RouteActivityType} pour une commande donnée depuis {@link ordersActivitiesMap}
     * ou effectue un appel GraphQL pour récupérer son identifiant de type d'activité,
     * puis récupère le code de type d'activité depuis {@link activitiesList} pour récupérer son {@link RouteActivityType} correspondant
     * @param orderId Identifiant de la commande à partir de laquelle on veut récupérer son {@link RouteActivityType}
     * @private
     */
    private getRouteActivityTypeFromOrderId$(orderId: number): Observable<RouteActivityType | undefined> {
        if (this.ordersActivitiesMap.has(orderId)) {
            return of(this.ordersActivitiesMap.get(orderId));
        }

        return this.graphqlService.mutateOne<'GetIdActiviteCommande', MutationGetIdActiviteCommandeArgs>({
            mutation: gql`
                mutation GetIdActiviteCommande($contexte: ContextInfosInput, $idCommande: Float) {
                    GetIdActiviteCommande(
                        contexte: $contexte
                        idCommande: $idCommande
                    )
                }
            `,
            variables: {
                idCommande: orderId
            }
        }).pipe(
            switchMap(activityId => this.getRouteActivityTypeFromActivityId$(activityId)),
            tap(routeActivityType => this.ordersActivitiesMap.set(orderId, routeActivityType))
        );
    }

    private getActivitiesList$(): Observable<ActiviteDto[]> {
        return this.graphqlService.mutateOne<'GetListeActiviteGenerique', MutationGetListeActiviteGeneriqueArgs>({
            mutation: gql`
                mutation GetListeActiviteGenerique(
                    $contexte: ContextInfosInput,
                    $criteres: CriteresActiviteDTOInput
                ){
                    GetListeActiviteGenerique(
                        contexte: $contexte,
                        criteres: $criteres
                    ) {
                        ActiviteDTO {
                            Id
                            Libelle
                            TypeActivite {
                                Code
                            }
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    IsCloture: false,
                    TakeTypeActivite: true
                }
            }
        }).pipe(
            map((GetListeActiviteGenerique) => GetListeActiviteGenerique.ActiviteDTO || []),
            tap(activitiesList => {
                this.activitiesList = activitiesList
                    .map(activity => ({
                        id: activity.Id,
                        typeCode: activity.TypeActivite.Code
                    }));
            }),
        );
    }
}
